// Word packs — themed sets of 5 words. Replaces the old "one lesson = both
// vocab AND mechanic" coupling. Now the player picks a pack (the WHAT) and
// then a teaching level (the HOW). See teaching-levels.ts for the mechanics.
//
// First pack ("People") is the recommended starter.

import type { ThemeId, TimeOfDay, WordDTO } from "@shared/types";
import { wordImageUrl } from "@shared/seed-images";

export interface WordPack {
  id: string;
  title: string;
  /** Short hint for the picker tile, e.g. "5 family + school words". */
  subtitle: string;
  /** Backdrop theme used when this pack is played in any level. */
  theme: ThemeId;
  timeOfDay: TimeOfDay;
  words: WordDTO[];
  /** Order in which packs appear in the picker. */
  orderIndex: number;
}

interface RawWord {
  id: string;
  english: string;
  vietnamese: string;
  sentence: string;
  /** OpenMoji codepoint, or null when no good emoji exists. */
  code: string | null;
}

function w(id: string, english: string, vietnamese: string, sentence: string, code: string | null): RawWord {
  return { id, english, vietnamese, sentence, code };
}

function ttsUrl(text: string): string | null {
  if (!text) return null;
  const slug = text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `/audio/tts/${slug}.mp3`;
}

function buildPack(meta: Omit<WordPack, "words"> & { rawWords: RawWord[] }): WordPack {
  return {
    ...meta,
    words: meta.rawWords.map((rw) => ({
      id: rw.id,
      english: rw.english,
      vietnamese: rw.vietnamese,
      sentence: rw.sentence,
      imageUrl: wordImageUrl(rw.code),
      audioUrl: ttsUrl(rw.english),
      sentenceAudioUrl: ttsUrl(rw.sentence),
      orderIndex: 0,
    })),
  };
}

export const WORD_PACKS: WordPack[] = [
  buildPack({
    id: "pack_people",
    title: "People",
    subtitle: "Family + school",
    theme: "candy",
    timeOfDay: "morning",
    orderIndex: 1,
    rawWords: [
      w("w_mother", "mother", "mẹ", "This is my mother.", "1F931"),
      w("w_father", "father", "bố", "This is my father.", "1F9D4"),
      w("w_brother", "brother", "anh trai", "This is my brother.", "1F466"),
      w("w_sister", "sister", "chị gái", "This is my sister.", "1F467"),
      w("w_friend", "friend", "bạn", "You are my friend.", "1FAC2"),
    ],
  }),
  buildPack({
    id: "pack_classroom",
    title: "Classroom",
    subtitle: "Things at school",
    theme: "forest",
    timeOfDay: "morning",
    orderIndex: 2,
    rawWords: [
      w("w_book", "book", "sách", "I have a book.", "1F4D8"),
      w("w_pen", "pen", "bút", "I have a pen.", "1F58A"),
      w("w_pencil", "pencil", "bút chì", "I write with a pencil.", "270F"),
      w("w_bag", "bag", "cặp", "My bag is blue.", "1F392"),
      w("w_chair", "chair", "ghế", "I sit on a chair.", "1FA91"),
    ],
  }),
  buildPack({
    id: "pack_food",
    title: "Food and Drink",
    subtitle: "Yummy things",
    theme: "candy",
    timeOfDay: "afternoon",
    orderIndex: 3,
    rawWords: [
      w("w_apple", "apple", "táo", "I have an apple.", "1F34E"),
      w("w_bread", "bread", "bánh mì", "I eat bread.", "1F35E"),
      w("w_milk", "milk", "sữa", "I drink milk.", "1F95B"),
      w("w_juice", "juice", "nước ép", "I drink juice.", "1F9C3"),
      w("w_rice", "rice", "cơm", "I eat rice.", "1F35A"),
    ],
  }),
  buildPack({
    id: "pack_colors",
    title: "Colors",
    subtitle: "Bright and bold",
    theme: "jungle",
    timeOfDay: "morning",
    orderIndex: 4,
    rawWords: [
      w("w_blue",   "blue",   "xanh dương", "The sky is blue.",     "1F7E6"),
      w("w_red",    "red",    "đỏ",         "The apple is red.",    "1F7E5"),
      w("w_green",  "green",  "xanh lá",    "The grass is green.",  "1F7E9"),
      w("w_yellow", "yellow", "vàng",       "The sun is yellow.",   "1F7E8"),
      w("w_orange", "orange", "cam",        "The orange is orange.","1F7E7"),
    ],
  }),
  buildPack({
    id: "pack_feelings",
    title: "Feelings",
    subtitle: "How you feel",
    theme: "desert",
    timeOfDay: "afternoon",
    orderIndex: 5,
    rawWords: [
      w("w_happy", "happy", "vui", "I am happy.", "1F642"),
      w("w_sad", "sad", "buồn", "I am sad.", "1F641"),
      w("w_angry", "angry", "tức giận", "I am angry.", "1F620"),
      w("w_tired", "tired", "mệt", "I am tired.", "1F634"),
      w("w_scared", "scared", "sợ hãi", "I am scared.", "1F628"),
    ],
  }),
  buildPack({
    id: "pack_numbers",
    title: "Numbers",
    subtitle: "Count from 1 to 5",
    theme: "space",
    timeOfDay: "night",
    orderIndex: 6,
    rawWords: [
      w("w_one_n", "one", "một", "I have one apple.", "0031-FE0F-20E3"),
      w("w_two", "two", "hai", "I have two pens.", "0032-FE0F-20E3"),
      w("w_three", "three", "ba", "I see three cats.", "0033-FE0F-20E3"),
      w("w_four", "four", "bốn", "I see four birds.", "0034-FE0F-20E3"),
      w("w_five", "five", "năm", "I have five apples.", "0035-FE0F-20E3"),
    ],
  }),
  buildPack({
    id: "pack_actions",
    title: "Action Words",
    subtitle: "Things you do",
    theme: "forest",
    timeOfDay: "morning",
    orderIndex: 7,
    rawWords: [
      w("w_run", "run", "chạy", "I run.", "1F3C3"),
      w("w_walk", "walk", "đi bộ", "I walk to school.", "1F6B6"),
      w("w_jump", "jump", "nhảy", "I jump.", "1F998"),
      w("w_eat", "eat", "ăn", "I eat bread.", "1F37D"),
      w("w_drink", "drink", "uống", "I drink milk.", "1F964"),
    ],
  }),
  buildPack({
    id: "pack_places",
    title: "Places + Things",
    subtitle: "Around the room",
    theme: "jungle",
    timeOfDay: "morning",
    orderIndex: 8,
    rawWords: [
      w("w_school", "school", "trường học", "I go to school.", "1F3EB"),
      w("w_table", "table", "cái bàn", "The book is on the table.", "TABLE"),
      w("w_door", "door", "cửa", "Open the door.", "1F6AA"),
      w("w_on", "on", "trên", "The book is on the table.", "2B06"),
      w("w_in", "in", "trong", "The fish is in water.", "1F4E5"),
    ],
  }),
];

export function getWordPack(id: string): WordPack | undefined {
  const builtin = WORD_PACKS.find((p) => p.id === id);
  if (builtin) return builtin;
  // Custom packs are user-created and stored in localStorage under the
  // same key used by local/customPacks.ts. We inline the lookup here so
  // this data module stays cycle-free.
  if (typeof window !== "undefined" && id.startsWith("pack_custom_")) {
    try {
      const raw = localStorage.getItem("wr_custom_packs_v1");
      if (!raw) return undefined;
      const arr = JSON.parse(raw) as WordPack[];
      return Array.isArray(arr) ? arr.find((p) => p.id === id) : undefined;
    } catch { /* ignore */ }
  }
  return undefined;
}
